// src/features/procedures/components/steps/YesNoStep.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  CheckCircle as YesIcon,
  Cancel as NoIcon,
} from '@mui/icons-material';

/**
 * Yes/No Step Component
 *
 * Both answers use the same neutral colour when selected: whether "no" is the
 * good answer depends on the question ("Any incidents?").
 * 
 * Options Config:
 * {
 *   "yes_label": "Ja, analysen er korrekt",    // Custom label for "Yes"
 *   "no_label": "Nej, jeg er uenig",           // Custom label for "No"
 *   "default_value": "yes"                      // Default selected value (optional)
 * }
 */
export default function YesNoStep({ step, value, onChange, disabled, preview }) {
  const options = step.options || {};
  const { t } = useTranslation(['procedures']);
  const yesLabel = options.yes_label || t('procedures:table_grid.yes', 'Ja');
  const noLabel = options.no_label || t('procedures:table_grid.no', 'Nej');
  // Older procedures store the default as a boolean
  const defaultValue = options.default_value === true ? 'yes'
    : options.default_value === false ? 'no'
      : options.default_value || null;

  const handleChange = (event, newValue) => {
    if (newValue !== null) {
      onChange(newValue);
    }
  };

  const handleToggleClick = (selectedValue) => () => {
    // Always trigger onChange, even if the value is already selected
    onChange(selectedValue);
  };

  return (
    <Box>
      <ToggleButtonGroup
        value={value || defaultValue}
        exclusive
        onChange={handleChange}
        disabled={disabled || preview}
        fullWidth
        sx={{
          mt: preview ? 0 : 1,
          // Two full-width answers read better as separate outlined buttons
          // than as the theme's segmented control (grey track)
          p: 0,
          gap: 1.5,
          bgcolor: 'transparent',
          '& .MuiToggleButton-root': {
            border: 1,
            borderColor: 'border.strong',
            borderRadius: 3,
            '&:hover': { bgcolor: 'action.hover' },
            '&.Mui-selected': { borderColor: 'primary.main', boxShadow: 'none' },
            // MUI's own disabled rule would otherwise recolour/remove the outline
            '&.Mui-disabled:not(.Mui-selected)': { border: 1, borderColor: 'border.strong' },
          },
        }}
      >
        <ToggleButton
          value="yes"
          onClick={handleToggleClick('yes')}
          sx={{
            py: 1.25,
            textTransform: 'none',
            '&.Mui-selected, &.Mui-selected:hover': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <YesIcon />
            <Typography variant="body2">{yesLabel}</Typography>
          </Box>
        </ToggleButton>

        <ToggleButton
          value="no"
          onClick={handleToggleClick('no')}
          sx={{
            py: 1.25,
            textTransform: 'none',
            '&.Mui-selected, &.Mui-selected:hover': {
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
            }
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <NoIcon />
            <Typography variant="body2">{noLabel}</Typography>
          </Box>
        </ToggleButton>
      </ToggleButtonGroup>
    </Box>
  );
}